import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { KeyRound, Copy, Trash2, Loader2, ExternalLink, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export const ApiKeysCard = () => {
  const { api } = useAuth();
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api().get('/api-keys');
      setKeys(res.data.keys || []);
    } catch (e) { /* noop */ }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const createKey = async () => {
    if (!name.trim()) { toast.error('Enter a name for the key'); return; }
    setCreating(true);
    try {
      const res = await api().post('/api-keys', { name: name.trim() });
      setNewKey(res.data);
      setName('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create key');
    } finally {
      setCreating(false);
    }
  };

  const revokeKey = async (id) => {
    try {
      await api().delete(`/api-keys/${id}`);
      toast.success('API key revoked');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Revoke failed');
    }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(newKey.api_key);
    toast.success('Key copied to clipboard');
  };

  const docsUrl = `${process.env.REACT_APP_BACKEND_URL}/api/docs`;

  return (
    <Card data-testid="api-keys-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5" /> API Access</CardTitle>
        <CardDescription>
          Generate personal API keys to call the public API endpoints with the <code className="text-xs bg-muted px-1 rounded">X-Api-Key</code> header.{' '}
          <a href={docsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary underline underline-offset-2" data-testid="api-docs-link-settings">
            Open API documentation <ExternalLink className="w-3 h-3" />
          </a>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Key name (e.g. CI pipeline)"
            className="max-w-xs" data-testid="api-key-name-input" />
          <Button onClick={createKey} disabled={creating} data-testid="api-key-generate-btn">
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
            Generate Key
          </Button>
        </div>

        {newKey && (
          <div className="p-4 rounded-lg border-2 border-amber-400 bg-amber-500/10 space-y-2" data-testid="api-key-new-box">
            <p className="text-sm font-semibold flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-4 h-4" /> Copy this key now — it will not be shown again.
            </p>
            <code className="block break-all text-sm bg-background p-2 rounded border" data-testid="api-key-new-value">{newKey.api_key}</code>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={copyKey} data-testid="api-key-copy-btn">
                <Copy className="w-4 h-4 mr-1" /> Copy
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setNewKey(null)} data-testid="api-key-done-btn">Done</Button>
            </div>
          </div>
        )}

        <div className="space-y-2" data-testid="api-keys-list">
          {keys.length === 0 && <p className="text-sm text-muted-foreground">No API keys yet.</p>}
          {keys.map((k) => (
            <div key={k.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border" data-testid={`api-key-row-${k.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium flex items-center gap-2">
                  {k.name}
                  {k.revoked && <Badge variant="secondary" className="bg-red-500/15 text-red-600">revoked</Badge>}
                </p>
                <p className="text-xs text-muted-foreground">
                  <code>{k.prefix}…</code> · created {new Date(k.created_at).toLocaleDateString()} · last used {k.last_used_at ? new Date(k.last_used_at).toLocaleString() : 'never'}
                </p>
              </div>
              {!k.revoked && (
                <Button variant="ghost" size="sm" onClick={() => revokeKey(k.id)} data-testid={`api-key-revoke-${k.id}`}>
                  <Trash2 className="w-4 h-4 mr-1 text-red-500" /> Revoke
                </Button>
              )}
            </div>
          ))}
        </div>

        <p className="text-xs text-muted-foreground">
          Example: <code className="bg-muted px-1 rounded">curl {process.env.REACT_APP_BACKEND_URL}/api/public/documents/&#123;id&#125; -H "X-Api-Key: dv_..."</code>
        </p>
      </CardContent>
    </Card>
  );
};
